import { useEffect } from "react";
import { useFeatureValue, useGrowthBook } from "@growthbook/growthbook-react";

const WORKFLOW_VARIANT_STORAGE_KEY = "booking-v4-workflow-variant";

const readStoredVariant = (): string | null => {
  try {
    return sessionStorage.getItem(WORKFLOW_VARIANT_STORAGE_KEY);
  } catch {
    // Storage unavailable (e.g. blocked in private mode) — behave as before.
    return null;
  }
};

/**
 * Returns the workflow variant ID from GrowthBook.
 *
 * Until GrowthBook's features have loaded (`gb.ready`), useFeatureValue only
 * echoes its fallback — that is NOT a real resolution. If that fallback were
 * a bare "default", every page reload mid-flow would flip the manifest/
 * step-config queryKeys "default" → real variant a few renders in; in that
 * window the redirect guards (page-layout / generic-step) fire against the
 * transient default manifest and bounce the user back into the wrong
 * variant's steps — observed as the availability page "reloading" back to
 * the standard flow whenever onSiteEstimateRequired was true. So the last
 * genuinely resolved variant is kept in sessionStorage and used as the
 * initial value on reload; once GrowthBook is ready, the live value wins
 * (and refreshes the stored one, including mid-session re-targeting).
 */
export const useWorkflowVariant = (): string => {
  const gb = useGrowthBook();
  const liveVariant = useFeatureValue("booking-workflow-variant", "default");
  const isReady = !!gb?.ready;

  useEffect(() => {
    if (!isReady) return;
    try {
      sessionStorage.setItem(WORKFLOW_VARIANT_STORAGE_KEY, liveVariant);
    } catch {
      // Storage unavailable — reloads just fall back to "default" as before.
    }
  }, [isReady, liveVariant]);

  if (isReady) {
    return liveVariant;
  }

  return readStoredVariant() ?? "default";
};
